import React from 'react';
import { FileText, Download, Loader2, AlertTriangle } from 'lucide-react';

export interface FicheDocument {
    id: string;
    kind: string | null;
    file_name: string;
    mime_type?: string | null;
    file_size?: number | null;
    created_at?: string | null;
}

/** Les trois natures que le parcours exige, dans l'ordre où on les rencontre. */
const NATURES: { kind: string; label: string; aide: string }[] = [
    { kind: 'plaquette', label: 'Plaquette commerciale', aide: 'Envoyée au client au tout début — elle fait foi sur les formules proposées.' },
    { kind: 'devis', label: 'Devis', aide: 'Produit dans votre logiciel habituel : la plateforme ne le génère pas, elle le conserve.' },
    { kind: 'devis_signe', label: 'Devis signé', aide: "C'est lui qui engage l'établissement et déclenche la collecte des menus." },
];

const fmtPoids = (o?: number | null) => (typeof o === 'number' && o > 0 ? `${Math.round(o / 1024)} Ko` : '');
const fmtJour = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; }
};

/**
 * Les justificatifs du dossier, rangés par nature plutôt qu'en vrac chronologique : la question qu'on se
 * pose ici n'est pas « qu'a-t-on déposé récemment ? » mais « le devis signé est-il arrivé ? ».
 *
 * Une nature peut porter plusieurs fichiers — un devis corrigé, une plaquette de la nouvelle saison. Le plus
 * récent est mis en avant, les précédents restent accessibles : on n'écrase jamais un justificatif, il a pu
 * être envoyé à un client tel quel.
 */
export function PipelineDocuments({ dossierId, documents }: { dossierId: string; documents: FicheDocument[] }) {
    const [ouverture, setOuverture] = React.useState<string | null>(null);
    const [erreur, setErreur] = React.useState('');

    // Le fichier est servi par une route authentifiée : on ne peut pas se contenter d'un lien, il faut
    // porter le jeton. Même geste que la fiche partenaire des Extras.
    const ouvrir = async (docId: string, fileName: string) => {
        setOuverture(docId);
        setErreur('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/suivi-privatisation/${dossierId}/documents/${docId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Document introuvable.');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'document';
            a.click();
            // Libéré au tour de boucle suivant : révoquer immédiatement annulerait le téléchargement.
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (e: any) {
            setErreur(e?.message || 'Téléchargement impossible.');
        } finally {
            setOuverture(null);
        }
    };

    const autres = documents.filter((d) => !d.kind || !NATURES.some((n) => n.kind === d.kind));

    return (
        <div className="space-y-2">
            {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}

            {NATURES.map((nature) => {
                const fichiers = documents.filter((d) => d.kind === nature.kind);
                const present = fichiers.length > 0;
                return (
                    <div key={nature.kind} className={present ? 'space-y-2' : `rounded-[9px] border border-dashed border-border px-[11px] py-[9px] flex items-center justify-between gap-3 flex-wrap`}>
                        {!present ? (
                            <>
                                <div className="min-w-0">
                                    <p className="text-[12px] font-semibold text-content-muted">{nature.label}</p>
                                    <p className="text-[10.5px] text-content-muted leading-relaxed">{nature.aide}</p>
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-[.06em] px-2 py-0.5 rounded-md bg-surface-soft text-content-muted whitespace-nowrap shrink-0">pas encore reçu</span>
                            </>
                        ) : fichiers.map((d, i) => (
                            <button key={d.id} type="button" onClick={() => ouvrir(d.id, d.file_name)} disabled={ouverture === d.id}
                                className="w-full flex items-center gap-2.5 px-[11px] py-[9px] rounded-[9px] bg-surface-soft border border-border-soft hover:border-border transition-colors text-left disabled:opacity-50">
                                <span className="w-[26px] h-[26px] shrink-0 rounded-[7px] bg-surface border border-border-soft text-content-muted flex items-center justify-center"><FileText size={13} /></span>
                                <span className="flex-1 min-w-0">
                                    <span className="block text-[12px] font-semibold text-content truncate">
                                        {nature.label}{i > 0 && <span className="text-content-muted font-normal"> — version précédente</span>}
                                    </span>
                                    <span className="block font-mono text-[10.5px] text-content-muted truncate">{d.file_name} · {fmtPoids(d.file_size)} · {fmtJour(d.created_at)}</span>
                                </span>
                                {ouverture === d.id ? <Loader2 size={13} className="animate-spin text-content-muted shrink-0" /> : <Download size={13} className="text-content-muted shrink-0" />}
                            </button>
                        ))}
                    </div>
                );
            })}

            {autres.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[12px] font-semibold text-content-muted flex items-center gap-1.5">
                        <AlertTriangle size={13} /> Autres documents
                    </p>
                    {/* Documents déposés hors parcours (ou avant l'existence du module, donc sans nature). */}
                    {autres.map((d) => (
                        <button key={d.id} type="button" onClick={() => ouvrir(d.id, d.file_name)}
                            className="w-full flex items-center gap-2.5 px-[11px] py-[9px] rounded-[9px] bg-surface-soft border border-border-soft hover:border-border transition-colors text-left">
                            <span className="w-[26px] h-[26px] shrink-0 rounded-[7px] bg-surface border border-border-soft text-content-muted flex items-center justify-center"><FileText size={13} /></span>
                            <span className="flex-1 min-w-0 text-[12px] font-semibold text-content truncate">{d.file_name}</span>
                            <Download size={13} className="text-content-muted shrink-0" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
